
import { useQuery } from "@tanstack/react-query";
import { createApiRequest } from "@/utils/apiUtils";
import { DashboardTrip, TripApiResponse } from "@/types/dashboard";
import { useMemo } from "react";

interface UseTripsProps {
  tripType: "booked" | "claimed" | "complete";
  enabled?: boolean;
}

export function useTrips({ tripType, enabled = true }: UseTripsProps) {
  const queryKey = ["trips", tripType];
  
  const fetchTrips = async () => {
    try {
      const endpoint = tripType === "booked" 
        ? `trips/?trip_state=BOOKED&ordering=-created`
        : `trips/?type=${tripType}`;
      console.log(`Fetching ${tripType} trips from endpoint: ${endpoint}`);
      
      const request = createApiRequest(endpoint);
      const response = await fetch(request);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error(`Error fetching ${tripType} trips:`, errorData);
        throw new Error(errorData.detail || `Failed to fetch ${tripType} trips`);
      }
      
      const data = await response.json();
      
      console.log(`✅ ${tripType} trips response:`, data);
      console.log(`📑 ${tripType} trips count:`, data.results ? data.results.length : 0);
      
      if (data.results && data.results.length > 0) {
        data.results.forEach((trip: any) => {
          console.log(`Trip ID ${trip.id}: status=${trip.status}, claimed_by=${trip.claimed_by}`);
        });
      }
      
      return data;
    } catch (error) {
      console.error(`Error in fetch${tripType.charAt(0).toUpperCase() + tripType.slice(1)}Trips:`, error);
      throw error;
    }
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: fetchTrips,
    enabled
  });

  // Process and sort trips
  const sortedTrips = useMemo(() => {
    if (!data?.results) return [];
    
    const trips = [...data.results];
    
    // For booked trips, preserve API order (no sorting)
    if (tripType === "booked") {
      return trips;
    }
    
    // For completed trips, ensure they have the complete status
    if (tripType === "complete") {
      return trips
        .map(trip => ({
          ...trip,
          status: "complete"
        }))
        .sort((a, b) => new Date(a.pickup_at).getTime() - new Date(b.pickup_at).getTime());
    }
    
    return trips.sort((a, b) => 
      new Date(a.pickup_at).getTime() - new Date(b.pickup_at).getTime()
    );
  }, [data, tripType]);

  return {
    trips: sortedTrips,
    isLoading,
    isError,
    error,
    refetch,
    rawData: data
  };
}
